import * as Sentry from "@sentry/browser";

const TAG = "[Sentry]";

// Hostnames used by local dev servers ("composeApp [wasmJs] Prod Debug" serves
// local.saturn.engineering/127.0.0.1, the staging dev task serves localhost).
// Deployed builds run on real domains, so this identifies local builds of any flavor.
const LOCAL_HOSTNAMES = ["localhost", "127.0.0.1", "0.0.0.0", "local.saturn.engineering"];

function isLocalBuild() {
    try {
        return LOCAL_HOSTNAMES.includes(self.location.hostname);
    } catch (error) {
        return false;
    }
}

let feedbackIntegrationInstance = null;

/**
 * Initialize Sentry with the given DSN, environment, and version.
 * Sets up error tracking with a user-triggered feedback integration.
 */
export function initSentry(dsn, env, version) {
    try {
        feedbackIntegrationInstance = Sentry.feedbackIntegration({
            autoInject: false,
            showBranding: false,
            colorScheme: "dark",
            showName: false,
            showEmail: false,
            enableScreenshot: true,
        });

        // Keep Sentry disabled on local builds (staging and prod alike): the SDK
        // initializes so feedback UI etc. keeps working, but no events are sent.
        const enabled = !isLocalBuild();

        Sentry.init({
            dsn: dsn,
            enabled: enabled,
            environment: env,
            release: `saturn-web@${version}`,
            integrations: [feedbackIntegrationInstance],
            // Filter out AbortError noise. These are raised by the browser whenever an
            // in-flight fetch is cancelled (user navigates away, coroutine cancels on
            // composable disposal, page unloads, etc.) and are not actionable bugs.
            ignoreErrors: ["AbortError", "The user aborted a request", "The operation was aborted", "signal is aborted without reason"],
            beforeSend(event, hint) {
                const error = hint && hint.originalException;
                if (error && (error.name === "AbortError" || (typeof error.message === "string" && error.message.includes("aborted")))) {
                    return null;
                }
                return event;
            },
        });
        console.log(TAG, `Initialized (env=${env}, version=${version}, enabled=${enabled})`);
    } catch (error) {
        console.error(TAG, "Failed to initialize:", error);
    }
}

/**
 * Set the current user context for Sentry events.
 */
export function setSentryUser(id, name, schoolId, isHighSchool) {
    try {
        Sentry.setUser({
            id: id,
            username: name || undefined,
            school_id: schoolId || undefined,
            is_high_school: isHighSchool,
        });
    } catch (error) {
        console.error(TAG, "Failed to set user:", error);
    }
}

/**
 * Clear the current user context.
 */
export function clearSentryUser() {
    try {
        Sentry.setUser(null);
    } catch (error) {
        console.error(TAG, "Failed to clear user:", error);
    }
}

/**
 * Open the Sentry Feedback dialog for the user to report a bug.
 */
export async function showBugReportDialog() {
    try {
        const feedback = Sentry.getFeedback();
        if (!feedback) {
            console.error(TAG, "Feedback integration not available");
            return;
        }
        const form = await feedback.createForm();
        form.appendToDom();
        form.open();
    } catch (error) {
        console.error(TAG, "Failed to show bug report dialog:", error);
    }
}

export async function captureUserFeedback(userName, userId, schoolId, isHighSchool, message) {
    try {
        Sentry.captureFeedback({
            name: userName,
            id: userId,
            school_id: schoolId || undefined,
            is_high_school: isHighSchool,
            message: message,
        });
    } catch (error) {
        console.error(TAG, "Failed to capture user feedback", error);
    }
}

/**
 * Capture an exception in Sentry with the given message and optional stack trace.
 */
export function captureException(message, stack) {
    try {
        const error = new Error(message);
        if (stack) {
            error.stack = stack;
        }
        Sentry.captureException(error);
    } catch (err) {
        console.error(TAG, "Failed to capture exception:", err);
    }
}
